import {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    AuthUser,
    fetchAuthSession,
    getCurrentUser,
    JWT,
    signOut
} from 'aws-amplify/auth';
import {AuthContext, AuthStatus} from '../context/AuthContext';


interface IAuthProvider {
    children?: ReactNode
}

export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {

    const [user, setUser] = useState<AuthUser | null>(null);
    const [status, setStatus] = useState<AuthStatus>('resolving');
    const [idToken, setIdToken] = useState<JWT | null | undefined>(null);
    const [accessToken, setAccessToken] = useState<JWT | null | undefined>(null);
    const [cognitoGroups, setCognitoGroups] = useState<string[] | null>(null);


    const loadCognitoUser = useCallback(async () => {


        try {
            const result = await getCurrentUser()

            const {
                username,
                userId,
                signInDetails
            } = result

            setUser({
                        username,
                        userId,
                        signInDetails
                    })
            setStatus('authenticated');


        } catch (error) {
            console.trace(
                'AUTH PROVIDER. FETCH USER ERROR',
                error
            );
            // Amplify throws here when nobody is signed in: this is how an
            // anonymous visit resolves. Without it the guard waits forever.
            setStatus('anonymous');
        }
    }, []);

    useEffect(() => {
        loadCognitoUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);


    const loadSession = useCallback(async () => {

        try {
            const {
                accessToken,
                idToken
            } = (await fetchAuthSession({forceRefresh: true})).tokens ?? {};

            setIdToken(idToken);
            setAccessToken(accessToken);
            if (typeof idToken !== 'undefined'
                && idToken.payload['cognito:groups'] !== undefined
                && Array.isArray(idToken.payload['cognito:groups'])
                && idToken.payload['cognito:groups'].length > 0) {
                setCognitoGroups(idToken.payload['cognito:groups'] as string[]);
            }

        } catch (error) {
            console.trace(
                'AUTH PROVIDER. FETCH SESSION ERROR',
                error
            );
            if (error instanceof Error && error.name === 'NotAuthorizedException') {
                try {
                    await signOut();
                } catch {
                    // ignore
                }
                window.location.href = '/';
            }
        }

    }, []);

    useEffect(() => {
        loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect: deliberately runs once, adding deps would re-fire it
    }, []);


    return (
        <AuthContext.Provider value={{
            user,
            status,
            idToken,
            accessToken,
            cognitoGroups,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
